import { approvalName } from "../entries/toolApproval";
import { actionDecision } from "../page/viewerActions";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../../errors/ErrorNotice";
import { isProblemWithCode } from "../../../errors/uiError";
import { useSubmission } from "../../../shared/state/useSubmission";
import { refusalText } from "../../../ui/feedback/refusalWords";
import ModalShell from "../../../ui/overlays/ModalShell";
import { useThreadContext } from "../page/threadPageContext";
import { applyHostingChoice } from "./automaticHosting";
import HostingChoiceForm from "./HostingChoiceForm";
import type { HostingChoice } from "./routeChoices";

interface AttachConnectorDialogProps {
  lastEntryNumber: number;
  onClose: () => void;
}

/**
 * The host makes the Thread live on one of their connectors, with the agent and model it
 * runs (T-3 settings, T-8). The route is a setting, so changing it is a logged change.
 */
export default function AttachConnectorDialog({ lastEntryNumber, onClose }: AttachConnectorDialogProps) {
  const { t } = useTranslation();
  const { threadId, overview, runApprovals, stepName } = useThreadContext();
  // Module C's decision (W-22 slice 4): a live Run moves to another PC only with no turn open.
  const moving = overview.state === "live" ? actionDecision(overview.viewer, "move_hosting") : null;
  // The view's overview, at the number the host saw (T-1).
  const thread = { ...overview, lastEntryNumber };
  const { state, submit } = useSubmission();
  // A failed save is said where the person looks: brought into view and focused (TR-B1b-02).
  const errorRef = useRef<HTMLDivElement>(null);
  const failed = state.status === "failed";
  useEffect(() => {
    if (!failed) return;
    errorRef.current?.scrollIntoView({ block: "nearest" });
    errorRef.current?.focus();
  }, [failed]);

  const apply = (choice: HostingChoice) => {
    submit(async () => {
      await applyHostingChoice(thread, choice);
      onClose();
    });
  };

  return (
    <ModalShell title={t("threads.hosting.attachTitle")} subtitle={t("threads.hosting.attachSubtitle")} onClose={onClose} size="lg">
      <div className="space-y-3">
        {state.status === "failed" ? (
          <div ref={errorRef} tabIndex={-1} className="outline-none">
            <ErrorNotice
              error={state.error}
              {...(isProblemWithCode(state.error, "thread_busy") && runApprovals.size > 0 ? {
                message: t("threads.hosting.approvalBlocksMove", { tools: [...runApprovals.values()].map((approval) => approvalName(approval, stepName, t("threads.toolApproval.agentsTool"))).join(", ") }),
              } : {})}
            />
          </div>
        ) : null}
        <HostingChoiceForm
          threadId={threadId}
          current={{ route: overview.settings.route, fallbackRoutes: overview.settings.fallback_routes ?? [], permissionProfile: overview.settings.permission_profile, connectorId: overview.attachedConnector }}
          submitLabel={t("threads.hosting.attach")}
          moveRefused={moving?.refusal == null ? undefined : refusalText(t, moving.refusal)}
          submitting={state.status === "submitting"}
          onSubmit={apply}
          onCancel={onClose}
        />
      </div>
    </ModalShell>
  );
}
